import { nameToRFC1035 } from "@outschool/environment";
import { v4 as uuid } from "uuid";

import {
  FILESTACK_CDN,
  FILESTACK_TRANSFORM,
  FileAccessTier,
  OUTSCHOOL_CDN_PREFIX,
  OUTSCHOOL_FILESTACK_GENERAL_PREFIX_WITHOUT_SLASH,
  OUTSCHOOL_FILESTACK_GENERAL_PREFIX_WITH_SLASH,
  OUTSCHOOL_FILESTACK_MIGRATION_PREFIX_WITHOUT_SLASH,
  OUTSCHOOL_FILESTACK_MIGRATION_PREFIX_WITH_SLASH,
  STAGE_FILES_PREFIX_WITHOUT_SLASH,
} from "./constants";

/**
 * s3 path stored in db has either migration bucket (outschool-filestack-migration)
 * prefix or plain path (for outschool-filestack-general bucket)
 */
export function constructUrl(s3path: string, cdn: string) {
  if (!s3path || !cdn) {
    throw new Error("s3path and cdn must be a non-empty string");
  }

  const bucketType = getBucketType(s3path);

  // need to do this so we can encode the path without the bucket prefix
  const pathWithoutBucket = removeBucketPrefix(s3path);

  // Decode first to handle already-encoded paths, then encode to ensure proper encoding
  // This prevents double-encoding if the path is already encoded in the database
  // Guard against malformed encodings to avoid throwing from decodeURIComponent.
  const decodedPath =
    safeDecodeURIComponent(pathWithoutBucket) ?? pathWithoutBucket;
  const encodedPath = encodeURIComponent(decodedPath);

  // add bucket back in
  const pathWithBucket = addBucketPrefix(encodedPath, bucketType);

  return `https://${cdn}/${pathWithBucket}`;
}

export function getBucketType(s3path: string): "migration" | "general" {
  if (s3path.startsWith(OUTSCHOOL_FILESTACK_MIGRATION_PREFIX_WITH_SLASH)) {
    return "migration";
  }
  return "general";
}

export function getBucketAndKeyFromS3Path(s3Path: string): {
  bucket: string;
  key: string;
} {
  const bucketType = getBucketType(s3Path);
  return {
    bucket:
      bucketType === "migration"
        ? OUTSCHOOL_FILESTACK_MIGRATION_PREFIX_WITHOUT_SLASH
        : OUTSCHOOL_FILESTACK_GENERAL_PREFIX_WITHOUT_SLASH,
    key: removeBucketPrefix(s3Path),
  };
}

export function getS3PathForStoringInDb(bucket: string, key: string): string {
  return bucket === OUTSCHOOL_FILESTACK_MIGRATION_PREFIX_WITHOUT_SLASH
    ? `${OUTSCHOOL_FILESTACK_MIGRATION_PREFIX_WITH_SLASH}${key}`
    : key;
}

export function removeBucketPrefix(s3path: string): string {
  if (s3path.startsWith(OUTSCHOOL_FILESTACK_MIGRATION_PREFIX_WITH_SLASH)) {
    return s3path.substring(
      OUTSCHOOL_FILESTACK_MIGRATION_PREFIX_WITH_SLASH.length,
    );
  }
  if (s3path.startsWith(OUTSCHOOL_FILESTACK_GENERAL_PREFIX_WITH_SLASH)) {
    return s3path.substring(
      OUTSCHOOL_FILESTACK_GENERAL_PREFIX_WITH_SLASH.length,
    );
  }
  return s3path; // No prefix, assume it's already the path without bucket
}

export function addBucketPrefix(
  s3path: string,
  bucketType: "migration" | "general",
): string {
  if (bucketType === "migration") {
    return `${OUTSCHOOL_FILESTACK_MIGRATION_PREFIX_WITH_SLASH}${s3path}`;
  }
  return `${OUTSCHOOL_FILESTACK_GENERAL_PREFIX_WITH_SLASH}${s3path}`;
}

function safeDecodeURIComponent(value: string) {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}

export function isFilestackUrl(url: string) {
  return url.startsWith(FILESTACK_CDN) || url.startsWith(FILESTACK_TRANSFORM);
}

/**
 * Generates the CDN URL for a file.
 * Returns empty string for secured files (they're not available via CDN).
 * This is a simpler version of constructUrl that doesn't encode the path.
 * @param bucket The S3 bucket name
 * @param s3Key The S3 key (file path)
 * @param accessTier The access tier (Public or Secured)
 * @param cdn The CDN domain (e.g., "cdn.outschool.com")
 */
export function generateCdnUrl(
  bucket: string,
  s3Key: string,
  accessTier: FileAccessTier,
  cdn: string,
): string {
  if (accessTier === FileAccessTier.Secured) {
    return "";
  }

  return `https://${cdn}/${bucket}/${s3Key}`;
}

export function isOutschoolUrl(url: string) {
  return url.startsWith(OUTSCHOOL_CDN_PREFIX);
}

/**
 * Sanitizes a directory path to only allow letters, numbers, underscores, hyphens, and slashes.
 * Removes spaces and any other invalid characters, then normalizes the path.
 */
export function sanitizeDirectory(directory: string): string {
  // Remove spaces and any characters that aren't letters, numbers, underscores, hyphens, or slashes
  let sanitized = directory.replace(/[^a-zA-Z0-9_\/-]/g, "");

  // Normalize multiple consecutive slashes to single slashes
  sanitized = sanitized.replace(/\/+/g, "/");

  // Remove leading and trailing slashes
  sanitized = sanitized.replace(/^\/+|\/+$/g, "");

  // Remove empty segments (e.g., "dir//subdir" becomes "dir/subdir")
  sanitized = sanitized
    .split("/")
    .filter(segment => segment.length > 0)
    .join("/");

  return sanitized;
}

/**
 * Generates a sanitized filename with UUID based on the original filename.
 * For public files: sanitizes the name and adds UUID + extension.
 * For secured files: returns just UUID.
 */
export function generateFileName(
  originalFileName: string,
  accessTier: FileAccessTier,
  directory?: string,
): string {
  if (accessTier === FileAccessTier.Secured) {
    return uuid();
  }

  const fileNameParts = originalFileName.split(".");
  const fileExt = fileNameParts.length > 1 ? fileNameParts.at(-1) : "";

  // Extract the name without extension
  const nameWithoutExt =
    fileNameParts.length > 1
      ? fileNameParts.slice(0, -1).join(".")
      : originalFileName || "file";

  // nameToRFC1035 removes non-alphanumeric characters (except hyphens) and converts PascalCase
  const sanitizedName = nameToRFC1035(nameWithoutExt);

  // Only add extension if it exists
  const fileName = fileExt
    ? `${sanitizedName}_${uuid()}.${fileExt}`
    : `${sanitizedName}_${uuid()}`;

  // If directory is provided, sanitize it and prepend to filename
  if (directory) {
    const sanitizedDirectory = sanitizeDirectory(directory);
    return sanitizedDirectory ? `${sanitizedDirectory}/${fileName}` : fileName;
  }

  return fileName;
}

export function generateEnvSpecificFileName(
  originalFileName: string,
  accessTier: FileAccessTier,
  directory?: string,
) {
  let fileName = generateFileName(originalFileName, accessTier, directory);
  fileName = OsPlatform.isProduction
    ? fileName
    : `${STAGE_FILES_PREFIX_WITHOUT_SLASH}/${fileName}`;

  return fileName;
}
